/* SPOTEC Career Apply — application form + application status (Oct 2026).
   Sends to the HRD Google Apps Script (Sheets + Drive). No login for applicants. */
(() => {
  if (customElements.get('spotec-career-apply')) return;

  const PHONE = /^(\+?62|0)8[0-9\s-]{7,14}$/;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const LINK = /^(https?:\/\/)?[^\s]+\.[^\s]+$/;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then((value) => { clearTimeout(timer); resolve(value); }, (error) => { clearTimeout(timer); reject(error); });
  });

  const readBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  customElements.define('spotec-career-apply', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.started = performance.now();
      this.endpoint = this.dataset.endpoint || '';
      this.maxMb = Number(this.dataset.maxMb || 5);
      this.form = this.querySelector('[data-sca-form]');
      this.select = this.querySelector('[data-sca-position]');
      this.fileBox = this.querySelector('[data-sca-file]');
      this.fileInput = this.fileBox.querySelector('input[type=file]');
      this.fileText = this.querySelector('[data-sca-file-text]');
      this.submit = this.querySelector('[data-sca-submit]');
      this.done = this.querySelector('[data-sca-done]');
      this.lookup = this.querySelector('[data-sca-lookup]');
      this.statusBox = this.querySelector('[data-sca-status]');

      this.fillPositions();
      this.addApplyButtons();
      this.bindFile();
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.send(); });
      this.form.addEventListener('input', (event) => this.clearError(event.target.name));
      this.form.addEventListener('change', (event) => this.clearError(event.target.name));
      this.lookup.addEventListener('submit', (event) => { event.preventDefault(); this.check(); });

      const params = new URLSearchParams(location.search);
      if (params.get('position')) this.choose(params.get('position'));
      if (params.get('app')) this.loadStatus({ t: params.get('app') }, true);
      else if (this.dataset.designMode === 'true') this.renderStatus(this.sample());
    }

    /* ---------- positions from the SPOTEC Career section ---------- */
    jobs() {
      return Array.from(document.querySelectorAll('spotec-career .sc-card')).map((card) => {
        const title = (card.querySelector('.sc-role') || {}).textContent || '';
        const meta = card.querySelector('.sc-open .sc-meta');
        return { title: title.trim(), department: meta ? meta.textContent.trim() : '', card };
      }).filter((job) => job.title);
    }

    fillPositions() {
      const other = this.select.querySelector('option:last-child');
      const seen = new Set();
      this.jobs().forEach((job) => {
        if (seen.has(job.title)) return;
        seen.add(job.title);
        const option = el('option', null, job.department ? job.title + ' · ' + job.department : job.title);
        option.value = job.title;
        this.select.insertBefore(option, other);
      });
    }

    choose(title) {
      const option = Array.from(this.select.options).find((item) => item.value.toLowerCase() === String(title).toLowerCase());
      if (option) { this.select.value = option.value; this.clearError('position'); }
    }

    addApplyButtons() {
      if (String(this.dataset.replaceSendCv).toLowerCase() !== 'true') return;
      this.jobs().forEach((job) => {
        const body = job.card.querySelector('.sc-modal-body');
        if (!body || body.querySelector('[data-sca-apply]')) return;
        // The old "Send CV to …" line (text without a link) is replaced by the Apply button.
        body.querySelectorAll('span.sc-apply').forEach((span) => { span.hidden = true; span.style.display = 'none'; });
        const button = el('button', 'sc-apply', this.dataset.applyLabel || 'Apply for this role');
        button.type = 'button';
        button.dataset.scaApply = job.title;
        button.addEventListener('click', () => {
          const dialog = button.closest('dialog');
          if (dialog && dialog.open) dialog.close();
          this.choose(job.title);
          this.showForm();
          requestAnimationFrame(() => {
            this.form.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
            this.form.querySelector('[name=name]').focus({ preventScroll: true });
          });
        });
        body.appendChild(button);
      });
    }

    /* ---------- CV file ---------- */
    bindFile() {
      const show = () => {
        const file = this.fileInput.files[0];
        this.fileBox.classList.toggle('has-file', !!file);
        this.fileText.textContent = file ? file.name + ' · ' + (file.size / 1048576).toFixed(1) + ' MB' : 'Choose file';
        if (file) this.checkFile(file, true);
      };
      this.fileInput.addEventListener('change', show);
      ['dragenter', 'dragover'].forEach((type) => this.fileBox.addEventListener(type, () => this.fileBox.classList.add('is-dragging')));
      ['dragleave', 'drop'].forEach((type) => this.fileBox.addEventListener(type, () => this.fileBox.classList.remove('is-dragging')));
    }

    checkFile(file, report) {
      let message = '';
      if (!file) message = 'Attach your CV as a PDF.';
      else if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') message = 'Attach your CV as a PDF file.';
      else if (file.size > this.maxMb * 1048576) message = 'Your CV is larger than ' + this.maxMb + ' MB. Save a smaller PDF and try again.';
      if (report) this.setError('cv', message);
      return !message;
    }

    /* ---------- errors ---------- */
    setError(name, message) {
      const box = this.querySelector('[data-error-for="' + name + '"]');
      if (box) { box.textContent = message || ''; box.hidden = !message; }
      const field = name === 'cv' ? null : this.form.elements[name];
      if (field && field.setAttribute) {
        if (message) { field.setAttribute('aria-invalid', 'true'); if (box) { box.id = box.id || 'ScaErr-' + name + '-' + Math.random().toString(36).slice(2, 7); field.setAttribute('aria-describedby', box.id); } }
        else { field.removeAttribute('aria-invalid'); field.removeAttribute('aria-describedby'); }
      }
      if (name === 'cv') { if (message) this.fileBox.setAttribute('data-invalid', ''); else this.fileBox.removeAttribute('data-invalid'); }
    }
    clearError(name) { if (name) this.setError(name, ''); this.setError('form', ''); }

    validate() {
      const f = this.form.elements;
      const errors = {};
      if (f.name.value.trim().length < 3) errors.name = 'Enter your full name.';
      if (!EMAIL.test(f.email.value.trim())) errors.email = 'Enter a valid email address.';
      if (!PHONE.test(f.whatsapp.value.trim())) errors.whatsapp = 'Enter a WhatsApp number that starts with 08 or +62.';
      if (!f.city.value.trim()) errors.city = 'Enter your city.';
      if (!f.position.value) errors.position = 'Choose a position.';
      if (f.portfolio.value.trim() && !LINK.test(f.portfolio.value.trim())) errors.portfolio = 'Enter a valid link, or leave it empty.';
      if (!f.consent.checked) errors.consent = 'Tick the box to agree before sending.';
      if (!this.checkFile(this.fileInput.files[0], false)) errors.cv = this.fileInput.files[0] ? 'Attach a PDF up to ' + this.maxMb + ' MB.' : 'Attach your CV as a PDF.';
      ['name', 'email', 'whatsapp', 'city', 'position', 'portfolio', 'consent', 'cv'].forEach((key) => this.setError(key, errors[key] || ''));
      const first = Object.keys(errors)[0];
      if (first) {
        const target = first === 'cv' ? this.fileInput : f[first];
        if (target && target.focus) target.focus();
        return false;
      }
      return true;
    }

    busy(button, on, text) {
      button.setAttribute('aria-busy', String(on));
      const label = button.querySelector('[data-sca-submit-text]');
      if (label && text) label.textContent = text;
    }

    /* ---------- send application ---------- */
    async send() {
      this.setError('form', '');
      if (!this.validate()) return;
      if (!this.endpoint) { this.setError('form', 'The application form is not connected yet. Please try again later.'); return; }
      const f = this.form.elements;
      const original = this.submit.querySelector('[data-sca-submit-text]').textContent;
      this.busy(this.submit, true, 'Sending…');
      try {
        const file = this.fileInput.files[0];
        const payload = {
          action: 'apply',
          name: f.name.value.trim(),
          email: f.email.value.trim(),
          whatsapp: f.whatsapp.value.trim(),
          city: f.city.value.trim(),
          position: f.position.value,
          portfolio: f.portfolio.value.trim(),
          consent: f.consent.checked,
          website: f.website.value,
          elapsed: Math.round(performance.now() - this.started),
          page: location.href.split('#')[0],
          cv: { name: file.name, type: 'application/pdf', data: await readBase64(file) }
        };
        // text/plain body = no CORS preflight, which Apps Script cannot answer.
        const response = await withTimeout(fetch(this.endpoint, { method: 'POST', body: JSON.stringify(payload), redirect: 'follow' }), 60000);
        const result = await response.json();
        if (!result.ok) {
          const message = result.error || 'We couldn\'t send your application. Please try again.';
          const field = result.field && this.querySelector('[data-error-for="' + result.field + '"]') ? result.field : 'form';
          this.setError(field, message);
          const target = field === 'cv' ? this.fileInput : this.form.elements[field];
          if (target && target.focus) target.focus();
          return;
        }
        this.showDone(result);
      } catch (error) {
        this.setError('form', 'We couldn\'t send your application. Check your connection and try again.');
      } finally {
        this.busy(this.submit, false, original);
      }
    }

    showDone(result) {
      this.form.hidden = true;
      this.done.hidden = false;
      this.querySelector('[data-sca-code]').textContent = result.code || '';
      const link = this.querySelector('[data-sca-status-link]');
      const local = new URL(location.href);
      if (result.token) local.searchParams.set('app', result.token);
      local.hash = 'application-status';
      link.href = local.pathname + local.search + local.hash;
      link.onclick = (event) => {
        event.preventDefault();
        history.replaceState(history.state, '', link.href);
        this.loadStatus({ t: result.token }, true);
      };
      this.done.focus({ preventScroll: true });
      this.done.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    }

    showForm() {
      if (!this.form.hidden) return;
      this.form.reset();
      this.fileInput.dispatchEvent(new Event('change'));
      this.form.hidden = false;
      this.done.hidden = true;
      this.started = performance.now();
    }

    /* ---------- application status ---------- */
    check() {
      const email = this.lookup.elements.email.value.trim();
      const code = this.lookup.elements.code.value.trim().toUpperCase();
      if (!EMAIL.test(email) || !/^SPX-[A-Z0-9]{4}-[A-Z0-9]{2}$/.test(code)) {
        this.setError('lookup', 'Enter the email you applied with and a code like SPX-7K4Q-29.');
        return;
      }
      this.loadStatus({ email, code }, false);
    }

    async loadStatus(query, scroll) {
      this.setError('lookup', '');
      if (!this.endpoint) { this.setError('lookup', 'Status checking is not connected yet.'); return; }
      const button = this.lookup.querySelector('button');
      this.busy(button, true);
      try {
        const url = new URL(this.endpoint);
        url.searchParams.set('a', 'status');
        Object.keys(query).forEach((key) => url.searchParams.set(key, query[key]));
        const response = await withTimeout(fetch(url.toString(), { redirect: 'follow' }), 20000);
        const result = await response.json();
        if (!result.ok) { this.statusBox.hidden = true; this.setError('lookup', result.error || 'We couldn\'t find this application.'); return; }
        this.renderStatus(result);
        if (scroll) this.statusBox.closest('section').scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      } catch (error) {
        this.setError('lookup', 'We couldn\'t load the status. Check your connection and try again.');
      } finally {
        this.busy(button, false);
      }
    }

    renderStatus(data) {
      const box = this.statusBox;
      box.textContent = '';
      box.appendChild(el('p', 'sca__status-meta', 'Application ' + (data.code || '')));
      box.appendChild(el('h3', null, data.firstName ? 'Hi ' + data.firstName + ', here\'s your status' : 'Your application status'));
      box.appendChild(el('p', 'sca__status-pos', data.position || ''));
      if (data.message) box.appendChild(el('p', 'sca__status-msg', data.message));
      const list = el('ol', 'sca__steps');
      (data.steps || []).forEach((step) => {
        const item = el('li', 'sca__step');
        item.dataset.state = step.state || 'next';
        if (step.state === 'current') item.setAttribute('aria-current', 'step');
        item.appendChild(el('span', 'sca__dot'));
        const body = el('div', 'sca__step-body');
        body.appendChild(el('div', 'sca__step-title', step.label));
        const note = [step.date, step.note].filter(Boolean).join(' · ');
        if (note) body.appendChild(el('div', 'sca__step-note', note));
        if (step.state === 'skipped') body.appendChild(el('span', 'visually-hidden', ' (skipped)'));
        item.appendChild(body);
        list.appendChild(item);
      });
      box.appendChild(list);
      box.hidden = false;
    }

    sample() {
      return {
        code: 'SPX-7K4Q-29', firstName: 'Rina', position: 'Example position', message: '',
        steps: [
          { label: 'Application received', date: '10 Oct 2026', state: 'done' },
          { label: 'CV reviewed', state: 'done' },
          { label: 'Screening', date: '14 Oct 2026', note: 'In progress. We may contact you on WhatsApp.', state: 'current' },
          { label: 'Interview', state: 'next' },
          { label: 'Decision', state: 'next' }
        ]
      };
    }
  });
})();
